import { AlertCircle, ArrowLeft, ClipboardCopy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  Sentry.captureException(error);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(error?.message ?? "Unknown error");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate("/dashboard");
  };

  return (
    <div className="bg-dark-background-main flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="rounded-2xl border border-[#E1E5EB] bg-white p-8 shadow-sm">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img
              src="/images/logo-transparent-light.svg"
              alt="Vaulta"
              className="h-8"
            />
          </div>

          {/* Error icon */}
          <div className="mb-6 flex flex-col items-center gap-4 text-center">
            <div className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-16 w-16 animate-ping rounded-full bg-[#ff6366]/20" />
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[#ff6366]/10">
                <AlertCircle className="size-8 text-[#ff6366]" strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <h1 className="text-dark-text-400 text-xl font-semibold">
                Something went wrong
              </h1>
              <p className="text-dark-text-300 mt-1.5 text-sm leading-5">
                An unexpected error occurred. Our team has been notified and
                we're working on a fix.
              </p>
            </div>
          </div>

          {/* Dev-only error details */}
          {import.meta.env.DEV && (
            <details className="mb-6 rounded-xl border border-[#E1E5EB] bg-[#FBFBFB] text-xs">
              <summary className="text-dark-text-300 flex cursor-pointer select-none items-center justify-between px-4 py-3 font-medium">
                <span>Error details</span>
                <span className="rounded-full bg-[#ff6366]/10 px-2 py-0.5 text-[10px] text-[#ff6366]">
                  Dev only
                </span>
              </summary>
              <div className="border-t border-[#E1E5EB] px-4 py-3">
                <pre className="text-dark-text-300 overflow-auto whitespace-pre-wrap font-mono text-[10px] leading-4">
                  {error?.message}
                </pre>
              </div>
            </details>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={resetErrorBoundary}
              className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white"
              style={{ backgroundColor: "#CB9CE0" }}
            >
              <RefreshCw className="size-4" />
              Try Again
            </Button>

            <Button
              onClick={handleGoHome}
              variant="outline"
              className="border-[#E1E5EB] text-dark-text-400 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold"
            >
              <ArrowLeft className="size-4" />
              Back to Dashboard
            </Button>

            <button
              onClick={handleCopy}
              className="text-dark-text-300 flex w-full items-center justify-center gap-1.5 py-2 text-xs transition-colors hover:text-[#CB9CE0]"
            >
              <ClipboardCopy className="size-3.5" />
              {copied ? "Copied!" : "Copy error message"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-dark-text-300 mt-6 text-center text-xs">
          If this keeps happening, please{" "}
          <a
            href="mailto:support@vaulta.com"
            className="text-dark-primary-main font-medium underline-offset-2 hover:underline"
          >
            contact support
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default ErrorFallback;
